/**
 * VARIATION_ITEMS_SECTION.JSX - Variation Items and Hierarchy Component
 * 
 * SUMMARY:
 * Presentational component for rendering the hierarchical structure of sections and items.
 * Displays expandable/collapsible sections with nested children and items.
 * 
 * PROPS:
 * @param {Array} subTaskArray - All subtasks/items
 * @param {Object} fields - Form fields (for profit margin)
 * @param {boolean} editMode - Whether in edit mode
 * @param {Function} enableEditBtn - Check if edit is enabled
 * @param {Object} hierarchyFunctions - Hierarchy-related functions
 * @param {Object} itemFunctions - Item-related functions
 * @param {Object} projectSummary - Project summary data
 * @param {Array} categoryList - Category list
 * @param {Array} unitList - Unit list
 * @param {Array} laborsList - Labors list
 * @param {boolean} wTemplatePermission - Template write permission
 * 
 * @returns {JSX.Element} Items section with hierarchy
 */

import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Accordion from 'react-bootstrap/Accordion';
import _ from 'lodash';
import idx from 'idx';
import { useDispatch } from 'react-redux';
import SubTask from "../../add-task/subtask";
import { updateSubTaskAction } from "../../../../slice/add-task";
import { toUSD, toPercent, numberFormat } from "../../../../util/currency-formatter";
import DeleteIcon from '@mui/icons-material/Delete';
import SectionSelectionModal from './SectionSelectionModal';
import './VariationItemsSection.scss';

export default function VariationItemsSection({
    subTaskArray,
    fields,
    editMode,
    enableEditBtn,
    hierarchyFunctions,
    itemFunctions,
    projectSummary,
    categoryList,
    unitList,
    laborsList,
    wTemplatePermission
}) {
    const dispatch = useDispatch();

    // Modal state for copy/move
    const [copyMoveModal, setCopyMoveModal] = useState({
        open: false,
        mode: 'copy', // 'copy' or 'move'
        itemId: null,
        itemData: null
    });

    const {
        expandedSections,
        buildHierarchy,
        toggleSection,
        getAllowedChildType,
        calculateSectionTotals
    } = hierarchyFunctions;

    const {
        handleAddTask,
        handleItemTemplateDialog,
        handleSaveTemplateDialog,
        deleteSubTaskHandler,
        getDeleteSubTask,
        handleSectionDelete,
        onEditSection,
        handleCopyItem,
        handleMoveItem
    } = itemFunctions;

    /**
     * Open copy/move modal with item data
     */
    const openCopyMoveModal = (item, mode) => {
        setCopyMoveModal({
            open: true,
            mode,
            itemId: item.id,
            itemData: item
        });
    };

    /**
     * Close copy/move modal
     */
    const closeCopyMoveModal = () => {
        setCopyMoveModal({
            open: false,
            mode: 'copy',
            itemId: null,
            itemData: null
        });
    };

    /**
     * Handle section selection from modal
     */
    const handleSectionSelect = (targetParentId) => {
        if (copyMoveModal.mode === 'copy') {
            handleCopyItem(copyMoveModal.itemId, targetParentId);
        } else {
            handleMoveItem(copyMoveModal.itemId, targetParentId);
        }
    };

    /**
     * Render section headers recursively with their hierarchies
     */
    const renderSectionHeaders = (items, level = 0) => {
        if (!items || items.length === 0) return null;

        return items.map((item) => {
            if (!item.is_section) return null;

            const hasChildren = item.children && item.children.length > 0;
            const itemIndex = subTaskArray.findIndex(st => st.id === item.id);
            const allowed = getAllowedChildType(item.id);
            const sectionTotals = calculateSectionTotals(item.id);

            const safeNum = (v) => parseFloat(v || 0);
            const material = {
                sale: safeNum(idx(sectionTotals, _ => _.material.sale)),
                cost: safeNum(idx(sectionTotals, _ => _.material.cost)),
                profit: safeNum(idx(sectionTotals, _ => _.material.profit)),
            };
            const labor = {
                sale: safeNum(idx(sectionTotals, _ => _.labor.sale)),
                cost: safeNum(idx(sectionTotals, _ => _.labor.cost)),
                profit: safeNum(idx(sectionTotals, _ => _.labor.profit)),
                forecastHours: safeNum(idx(sectionTotals, _ => _.forecastHours)),
            };
            const subcontractor = {
                sale: safeNum(idx(sectionTotals, _ => _.subcontractor.sale)),
                cost: safeNum(idx(sectionTotals, _ => _.subcontractor.cost)),
                profit: safeNum(idx(sectionTotals, _ => _.subcontractor.profit)),
            };
            const grand = {
                sale: safeNum(idx(sectionTotals, _ => _.total)),
                cost: safeNum(idx(sectionTotals, _ => _.subtotal)),
                profit: safeNum(idx(sectionTotals, _ => _.total_profit)),
            };

            return (
                <Accordion key={item.id} className="section-wrapper mb-3" style={{ marginLeft: `${level * 10}px` }} data-cy={`section-accordion-${item.id}`}>
                    <Accordion.Item eventKey={item.id} className="border accordion-item">
                        <Accordion.Header
                            className="px-2 py-2 accordion-header"
                        >
                            <div className="row w-100 align-items-center g-0">
                                {/* Left: accordion arrow + name */}
                                <div className="col-lg-4 col-md-12 d-flex align-items-center">
                                    <div className="flex-grow-1 me-4">
                                        <div className="section-name-text" style={{ lineHeight: 1 }}>
                                            {editMode ? (
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm section-name-input"
                                                    placeholder="Section name..."
                                                    value={item.description || ''}
                                                    onChange={(e) => {
                                                        const _temp = _.cloneDeep(subTaskArray);
                                                        _temp[itemIndex].description = e.target.value;
                                                        _temp[itemIndex].descriptionError = !e.target.value;
                                                        dispatch(updateSubTaskAction(_temp));
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    data-cy="section-name-input"
                                                />
                                            ) : (
                                                <span data-cy="section-name-display">{item.description || '(Unnamed Section)'}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right: material/resource/contractor/total aligned to SubTask table */}
                                <div className="col-lg-7 col-md-12">
                                    <div className="table-responsive variation-summary-table variation-table-subtask-header">
                                        <Table bordered className="table-create mb-0 tbl-fixed tbl-fixed-width variation-table-subtask">
                                            <tbody>
                                                <tr className="section-totals-sale" data-cy="section-totals-sale">
                                                    <td></td>
                                                    <td><strong>{toUSD(material.sale)}</strong></td>
                                                    <td><strong>{toUSD(subcontractor.sale)}</strong></td>
                                                    <td><strong>{toUSD(labor.sale)}</strong></td>
                                                    <td><strong>{numberFormat(labor.forecastHours || 0, true) + " hrs"}</strong></td>
                                                    <td><strong>{toUSD(grand.sale)}</strong></td>
                                                </tr>
                                                <tr className="section-totals-profit" data-cy="section-totals-profit">
                                                    <td></td>
                                                    <td>{toUSD(material.profit)} ({toPercent(material.profit, material.cost)})</td>
                                                    <td>{toUSD(subcontractor.profit)} ({toPercent(subcontractor.profit, subcontractor.cost)})</td>
                                                    <td>{toUSD(labor.profit)} ({toPercent(labor.profit, labor.cost)})</td>
                                                    <td></td>
                                                    <td>{toUSD(grand.profit)} ({toPercent(grand.profit, grand.cost)})</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                                {/* Right: options menu */}
                                <div className="col-lg-1 d-flex justify-content-end">
                                    {editMode && (
                                        <span
                                            className="d-flex align-items-center"
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ cursor: 'pointer' }}
                                            data-cy="section-delete-btn"
                                        >
                                            <DeleteIcon className="text-danger" onClick={() => handleSectionDelete(item)} />
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Accordion.Header>

                        <Accordion.Body className="px-0 py-2">
                            {/* Actions Row */}
                            {editMode && (
                                <div className="section-actions-row px-2 py-1 mb-2">
                                    <div className="row w-100 g-0">
                                        <div className="col-lg-4 d-none d-lg-block"></div>
                                        <div className="col-lg-7 d-flex justify-content-end">
                                            <div className="d-flex gap-1">
                                                {allowed.canAddSection && (
                                                    <Button
                                                        size="sm"
                                                        className='secondarybtn add-section-btn'
                                                        onClick={() => handleAddTask(true, item.id)}
                                                        title="Add nested section"
                                                        data-cy="add-nested-section-btn"
                                                    >
                                                        Add Section
                                                    </Button>
                                                )}
                                                {allowed.canAddItem && (
                                                    <Button
                                                        size="sm"
                                                        className='secondarybtn add-item-btn'
                                                        onClick={() => handleAddTask(false, item.id)}
                                                        title="Add item to this section"
                                                        data-cy="add-item-to-section-btn"
                                                    >
                                                        Add Item
                                                    </Button>
                                                )}
                                                {allowed.canAddItem && (
                                                    <Button
                                                        size="sm"
                                                        className='secondarybtn add-template-btn'
                                                        onClick={() => handleItemTemplateDialog(item.id)}
                                                        title="Add from template to this section"
                                                        data-cy="add-template-to-section-btn"
                                                    >
                                                        ADD TEMPLATE
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-lg-1 d-none d-lg-block"></div>
                                    </div>
                                </div>
                            )}

                            {/* Render Children */}
                            {hasChildren && (
                                <div className="section-children">
                                    {/* Nested sections */}
                                    {item.children.filter(child => child.is_section).map(child =>
                                        renderSectionHeaders([child], level + 1)
                                    )}

                                    {/* Items in this section */}
                                    {item.children.filter(child => !child.is_section).length > 0 && (
                                        <div className="section-items px-2">
                                            <SubTask
                                                subcontractors={idx(projectSummary, _ => _.subcontractors) || []}
                                                profitMargin={fields.profitMargin.value}
                                                profitMarginType={fields.profitMarginType.value}
                                                subTaskArray={subTaskArray}
                                                visibleItemIds={item.children.filter(child => !child.is_section).map(c => c.id)}
                                                isDisabled={!editMode}
                                                handleSaveTemplateDialog={handleSaveTemplateDialog}
                                                wTemplatePermission={wTemplatePermission}
                                                deleteSubTaskHandler={deleteSubTaskHandler}
                                                getDeleteSubTask={getDeleteSubTask}
                                                categoryList={categoryList}
                                                unitList={unitList}
                                                laborsList={laborsList}
                                                isVariation={true}
                                                editMode={editMode}
                                                enableEditBtn={enableEditBtn}
                                                onCopyItem={(item) => openCopyMoveModal(item, 'copy')}
                                                onMoveItem={(item) => openCopyMoveModal(item, 'move')}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            );
        });
    };

    // Build hierarchy
    const hierarchy = buildHierarchy();

    // Get root-level items (not in sections)
    const rootItemIds = subTaskArray
        .filter(item => !item.parent_subtask_id && !item.is_section)
        .map(item => item.id);

    // Get root-level allowed child types
    const rootAllowed = getAllowedChildType(null);

    return (
        <>
            {/* Empty State */}
            {subTaskArray.length === 0 ? (
                <div className="alert alert-info" data-cy="empty-items-alert">
                    <p className="mb-0">No items or sections yet. Click "ADD SECTION" or "ADD ITEM" to begin.</p>
                </div>
            ) : (
                <div className="hierarchy-container mb-3">
                    {/* Render section headers and their hierarchies */}
                    {renderSectionHeaders(hierarchy)}

                    {/* Render root-level items (not in any section) */}
                    {rootItemIds.length > 0 && (
                        <div className="root-items">
                            <SubTask
                                subcontractors={idx(projectSummary, _ => _.subcontractors) || []}
                                profitMargin={fields.profitMargin.value}
                                profitMarginType={fields.profitMarginType.value}
                                subTaskArray={subTaskArray}
                                visibleItemIds={rootItemIds}
                                isDisabled={!editMode}
                                handleSaveTemplateDialog={handleSaveTemplateDialog}
                                wTemplatePermission={wTemplatePermission}
                                deleteSubTaskHandler={deleteSubTaskHandler}
                                getDeleteSubTask={getDeleteSubTask}
                                categoryList={categoryList}
                                unitList={unitList}
                                laborsList={laborsList}
                                isVariation={true}
                                editMode={editMode}
                                enableEditBtn={enableEditBtn}
                                onCopyItem={(item) => openCopyMoveModal(item, 'copy')}
                                onMoveItem={(item) => openCopyMoveModal(item, 'move')}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Add Buttons - Contextual based on root level */}
            {editMode && (
                <div className="col-12 justify-content-end gap-2 d-flex btn-res">
                    {rootAllowed.canAddItem && (
                        <Button
                            onClick={() => handleItemTemplateDialog(null)}
                            className="primarybtn add-template-btn"
                            title="Add items from template"
                            data-cy="add-template-btn"
                        >
                            ADD TEMPLATE
                        </Button>
                    )}
                    {rootAllowed.canAddSection && (
                        <Button
                            onClick={() => handleAddTask(true, null)}
                            className="primarybtn"
                            title="Add a new section"
                            data-cy="add-section-btn"
                        >
                            ADD SECTION
                        </Button>
                    )}
                    {rootAllowed.canAddItem && (
                        <Button
                            onClick={() => handleAddTask(false, null)}
                            className="primarybtn add-item-btn"
                            title="Add a new item"
                            data-cy="add-item-btn"
                        >
                            ADD ITEM
                        </Button>
                    )}
                </div>
            )}

            {/* Section Selection Modal for Copy/Move */}
            <SectionSelectionModal
                open={copyMoveModal.open}
                onClose={closeCopyMoveModal}
                sections={subTaskArray}
                onSelectSection={handleSectionSelect}
                currentParentId={copyMoveModal.itemData?.parent_subtask_id || null}
                mode={copyMoveModal.mode}
                getAllowedChildType={getAllowedChildType}
            />
        </>
    );
}

